import { ArrowDown, ArrowRight, Database, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import EditorialSection from "@/components/EditorialSection";
import OperationalFlow from "@/components/OperationalFlow";
import ProductWordmark from "@/components/ProductWordmark";

const responseBenchmarks = [
  { label: "Hot data lookup", value: 99.9 },
  { label: "Random data lookup", value: 99.9 },
  { label: "Single event capture", value: 93.0 },
  { label: "Write-intensive mixed workload, 2 KiB records", value: 91.3 },
  { label: "Read-heavy operational workload", value: 34.8 },
  { label: "Balanced operational workload", value: 31.7 },
];

const scaleProof = [
  {
    label: "Endpoint state reads",
    quantum: "59,697",
    sqlite: "13,362",
    unit: "operations per second",
    outcome: "4.5× higher throughput",
  },
  {
    label: "Policy lookups",
    quantum: "54,246",
    sqlite: "18,022",
    unit: "operations per second",
    outcome: "3.0× higher throughput",
  },
  {
    label: "Full 1,000-endpoint fleet snapshot",
    quantum: "0.55 ms",
    sqlite: "18.42 ms",
    unit: "response time",
    outcome: "97% faster fleet visibility",
  },
  {
    label: "Heartbeat state updates",
    quantum: "9,593",
    sqlite: "5,432",
    unit: "updates per second",
    outcome: "77% higher burst throughput",
  },
  {
    label: "Reconnect state updates",
    quantum: "9,345",
    sqlite: "5,419",
    unit: "updates per second",
    outcome: "72% higher burst throughput",
  },
];

const largePayloadReads = [
  { label: "Hot point read", quantum: "under 0.01 ms", sqlite: "124 ms", outcome: "Point-read response" },
  { label: "Random point read", quantum: "under 0.01 ms", sqlite: "159 ms", outcome: "Point-read response" },
  { label: "Read-heavy operational workload", quantum: "46 ms", sqlite: "218 ms", outcome: "79% faster" },
  { label: "Balanced operational workload", quantum: "342 ms", sqlite: "546 ms", outcome: "37% faster" },
];

const technicalRows = [
  ["Hot data lookup", "0.001200 ms", "0.873800 ms", "99.9% faster"],
  ["Random data lookup", "0.001500 ms", "1.057620 ms", "99.9% faster"],
  ["Single event capture", "1.788130 ms", "25.577525 ms", "93.0% faster"],
  ["Write-intensive mixed workload, 2 KiB records", "1.789205 ms", "20.662830 ms", "91.3% faster"],
  ["Read-heavy operational workload", "0.833610 ms", "1.278220 ms", "34.8% faster"],
  ["Balanced operational workload", "1.469255 ms", "2.152485 ms", "31.7% faster"],
  ["Batch event capture", "32.726885 ms", "63.277690 ms", "48.3% faster"],
];

function Label({ children, dark = false }) {
  return (
    <div className={`inline-flex items-center gap-2 border px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[.16em] ${dark ? "border-white/25 text-white" : "border-ink-900 text-ink-900"}`}>
      <span className={`h-3 w-3 border-2 ${dark ? "border-electric bg-electric" : "border-electric bg-electric-soft"}`} />
      {children}
    </div>
  );
}

function ResponseChart() {
  return (
    <div className="border border-ink-900 bg-white" role="group" aria-labelledby="response-chart-title">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-ink-900 px-5 py-5 md:px-7">
        <div>
          <p className="lf-eyebrow">Measured engine workloads</p>
          <h3 id="response-chart-title" className="mt-2 font-display text-2xl font-black">Response-time reduction versus SQLite</h3>
        </div>
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[.14em] text-ink-600">
          <span className="h-3 w-3 bg-electric" aria-hidden="true" /> QuantumCubeDB compared with SQLite
        </div>
      </div>
      <ol className="divide-y divide-ink-100 px-5 py-2 md:px-7">
        {responseBenchmarks.map((item) => (
          <li key={item.label} className="grid gap-3 py-4 md:grid-cols-[minmax(220px,.85fr)_minmax(240px,1.6fr)_110px] md:items-center">
            <span className="text-sm font-medium text-ink-800">{item.label}</span>
            <div className="h-7 border-l border-ink-300 bg-ink-50" aria-hidden="true">
              <div className="h-full bg-electric" style={{ width: `${item.value}%` }} />
            </div>
            <strong className="font-display text-2xl leading-none text-electric">
              {item.value.toFixed(1)}% <span className="font-sans text-xs font-bold text-ink-600">faster</span>
            </strong>
          </li>
        ))}
      </ol>
      <p className="border-t border-ink-100 px-5 py-4 text-sm text-ink-600 md:px-7">100,000 records · 2 KiB payloads · 16 concurrent clients</p>
    </div>
  );
}

function ArchitecturePath({ title, tone, steps, description }) {
  return (
    <article className={`border p-6 md:p-7 ${tone === "data" ? "border-ink-200 bg-ink-50" : "border-electric bg-electric-soft/50"}`}>
      <div className="flex items-start gap-4">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center border ${tone === "data" ? "border-ink-900 bg-ink-900 text-white" : "border-electric bg-white text-electric"}`}>
          {tone === "data" ? <ShieldCheck className="h-4 w-4" /> : <Database className="h-4 w-4" />}
        </span>
        <div>
          <h3 className="font-display text-xl font-bold">{title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink-600">{description}</p>
        </div>
      </div>
      <ol className="mt-7 grid gap-2 md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] md:items-center">
        {steps.map((step, index) => (
          <li key={step} className="contents">
            <span className="border border-current bg-white px-3 py-3 text-center text-xs font-semibold text-ink-800">{step}</span>
            {index < steps.length - 1 ? <ArrowRight className="mx-auto hidden h-4 w-4 text-electric md:block" aria-hidden="true" /> : null}
          </li>
        ))}
      </ol>
    </article>
  );
}

export default function QuantumCubeDB() {
  return (
    <>
      <SEO
        title="QuantumCubeDB — Intelligence starts with better data."
        description="Meet QDB: fast operational state, FPGA telemetry processing and the data foundation for LightningFlow’s Central Nervous System."
        canonicalPath="/quantumcubedb"
        image="/quantumcubedb/qdb-official-wordmark-a0884f92.png"
      />

      <section className="lf-product-hero relative overflow-hidden bg-[#031124] text-white">
        <div className="absolute inset-0 lf-grid-bg-faint pointer-events-none opacity-20" />
        <div className="lf-container relative grid gap-12 py-20 md:py-28 lg:max-w-5xl">
          <div>
            <ProductWordmark product="quantumcubedb" size="hero" loading="eager" />
            <div className="lf-eyebrow text-cyan-200">QuantumCubeDB · The operational intelligence engine</div>
            <h1 className="mt-5 max-w-3xl font-display text-5xl font-black leading-[.92] tracking-[-.04em] md:text-7xl">Intelligence starts with <span className="text-cyan-200">better data.</span></h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-200 md:text-xl">Every endpoint. Every policy. Every signal that matters. QDB brings operational state into focus—so LightningFlow can understand what is happening, and what needs attention.</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a className="lf-btn border border-cyan-300 bg-cyan-300 text-ink-900 hover:border-white hover:bg-white" href="#operational-state">Discover QDB <ArrowDown className="h-4 w-4" /></a>
              <a className="lf-btn border border-white/40 text-white hover:border-white" href="#hardware-intelligence">Explore the FPGA architecture <ArrowDown className="h-4 w-4" /></a>
            </div>
            <p className="mt-7 max-w-2xl text-sm leading-relaxed text-slate-400">QuantumCubeDB is LightningFlow’s purpose-built database for endpoint, policy, health and fleet state.</p>
          </div>
        </div>
      </section>

      <section className="border-b border-ink-100 bg-white" aria-label="Measured QDB software engine performance">
        <div className="lf-container grid gap-8 py-10 md:grid-cols-3 md:py-14">
          {[
            ["4.5×", "Endpoint-state throughput"],
            ["3.0×", "Policy-lookup throughput"],
            ["97%", "Faster fleet snapshots"],
          ].map(([value, label]) => <div key={label}><p className="font-display text-5xl font-black tracking-tight text-electric">{value}</p><p className="mt-3 font-display text-lg font-bold">{label}</p></div>)}
          <p className="text-xs leading-relaxed text-ink-500 md:col-span-3">QDB software engine versus SQLite in measured operational workloads. <a href="#technical-details" className="underline underline-offset-4">See methodology.</a></p>
        </div>
      </section>

      <EditorialSection number="01" label="Made for the work that matters" title="From scattered signals. To a clear picture." body="A heartbeat tells you an endpoint is there. Policy tells you how it should behave. Health tells you whether it needs attention. QDB brings that context together, close to LightningFlow’s control plane.">
        <OperationalFlow title="Network activity → operational state → understanding → governed action" />
      </EditorialSection>

      <section id="hardware-intelligence" className="lf-section scroll-mt-20 bg-[#031124] text-white">
        <div className="lf-container">
          <p className="lf-eyebrow text-cyan-200">02 / Software intelligence. Purpose-built hardware.</p>
          <div className="mt-5 grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-end">
            <h2 className="max-w-3xl font-display text-4xl font-black leading-[.98] md:text-6xl">Give the right work <span className="text-cyan-200">its own engine.</span></h2>
            <p className="max-w-2xl text-lg leading-relaxed text-slate-200">An FPGA—a field-programmable gate array—is a chip whose logic can be configured for a specific task. LightningFlow’s FPGA work turns compact telemetry processing into a dedicated hardware pipeline, alongside QDB’s software engine.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              ["Process the signal", "Repeated telemetry operations map to configured logic. The verified hardware processes batches of 64-byte telemetry records and combines records into grouped results."],
              ["Bring back the meaning", "Grouping can turn many incoming records into a smaller set of operational summaries. That is the architectural opportunity: less repetitive work on the host, with useful state ready for the next decision."],
              ["Keep software in control", "The host manages work submission, memory ownership and completion. CPU processing remains part of the design; hardware output is checked against the software reference."],
            ].map(([title, body], index) => <article key={title} className="border-t border-white/20 pt-6"><p className="font-mono text-xs text-cyan-200">0{index + 1}</p><h3 className="mt-4 font-display text-2xl font-bold">{title}</h3><p className="mt-4 text-sm leading-relaxed text-slate-300">{body}</p></article>)}
          </div>
          <div className="mt-12 grid gap-8 border border-cyan-200/30 p-6 md:grid-cols-[.7fr_1.3fr] md:p-8">
            <div><p className="lf-eyebrow text-cyan-200">Verified on physical hardware</p><p className="mt-4 font-display text-4xl font-black">250 MHz</p><p className="mt-2 text-sm text-slate-300">Configured FPGA processing clock</p></div>
            <div><h3 className="font-display text-2xl font-bold">Real records. Matching results.</h3><p className="mt-4 text-sm leading-relaxed text-slate-300">Physical batch tests covered 4–64 KiB inputs, with full output matching the CPU reference. This establishes hardware processing correctness for those tests. The software benchmarks on this page are separate measurements; they do not claim an FPGA speedup or end-to-end platform performance.</p></div>
          </div>
          <Link to="/fpga-acceleration" className="mt-8 inline-flex items-center gap-2 font-semibold text-cyan-200 hover:text-white">Explore FPGA processing <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      <EditorialSection number="03" label="The Central Nervous System" title="Better context. Better decisions." body="CNS stands for Central Nervous System: LightningFlow’s architecture for connecting operational signals, evidence and governed action. QDB supplies the state layer—giving reasoning a foundation in recorded facts.">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            ["QDB", "Remember what matters.", "Endpoint state, policy revisions, health summaries and audit references give the platform operational context."],
            ["Sheila", "Understand the picture.", "The CNS design connects that context with Sheila’s analysis and recommendations, so decisions can be explained through evidence."],
            ["Command Centre", "Keep people in command.", "Operators review context and proposed actions. Approval and deterministic execution remain distinct from an AI recommendation."],
          ].map(([label, title, body]) => <article key={label} className="border border-ink-100 bg-white p-6 md:p-7"><p className="lf-eyebrow text-electric">{label}</p><h3 className="mt-4 font-display text-2xl font-bold">{title}</h3><p className="mt-4 text-sm leading-relaxed text-ink-600">{body}</p></article>)}
        </div>
        <div className="mt-8 flex flex-wrap gap-6">
          <Link to="/sheila-ai" className="inline-flex items-center gap-2 font-semibold text-electric">Meet Sheila <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/platform" className="inline-flex items-center gap-2 font-semibold text-electric">Explore the platform <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </EditorialSection>

      <section id="operational-state" className="lf-section scroll-mt-20 bg-ink-50/50">
        <div className="lf-container">
          <div className="grid gap-8 lg:grid-cols-[.8fr_1.2fr] lg:items-end">
            <div>
              <Label>04 / Architecture</Label>
              <h2 className="mt-5 font-display text-4xl font-black leading-[.98] md:text-6xl">Connected intelligence. Private by design.</h2>
            </div>
            <p className="max-w-2xl text-lg leading-relaxed text-ink-600">Operational intelligence needs context, not a copy of your traffic. QDB holds compact state records while encrypted customer data follows the secure data plane to its destination.</p>
          </div>

          <div className="mt-10 grid gap-4 lg:grid-cols-2">
            <ArchitecturePath
              title="Encrypted customer data"
              tone="data"
              description="Customer traffic moves securely through the data plane to its destination. QuantumCubeDB is not in the payload path."
              steps={["Halo endpoint", "LightningFlow secure data plane", "Destination"]}
            />
            <ArchitecturePath
              title="Live operational intelligence"
              tone="control"
              description="Compact state records keep the control plane and Command Centre informed and ready to respond."
              steps={["Halo endpoint", "LightningFlow control plane", "QuantumCubeDB", "Command Centre"]}
            />
          </div>

          <div className="mt-8 grid gap-7 border border-ink-100 bg-white p-6 md:grid-cols-[1.1fr_.9fr] md:p-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[.16em] text-ink-500">Operational intelligence includes</p>
              <ul className="mt-4 grid gap-2 text-sm leading-relaxed text-ink-700 sm:grid-cols-2">
                <li>Endpoint identity and enrolment references</li>
                <li>Current endpoint status</li>
                <li>Policy revision</li>
                <li>Connection and session state</li>
                <li>Health summaries</li>
                <li>Reconnect state</li>
                <li>Fleet intelligence</li>
                <li>Counters and audit references</li>
              </ul>
            </div>
            <div className="border-l-2 border-electric bg-electric-soft px-5 py-5 text-base font-medium leading-relaxed text-ink-800">Designed for compact operational state—not customer traffic, packet contents or transferred files.</div>
          </div>
          <div id="operational-scale" className="mt-16 scroll-mt-20 border-t border-ink-300 pt-12">
            <div className="grid gap-8 lg:grid-cols-[.72fr_1.28fr] lg:items-end">
              <div><p className="lf-eyebrow">05 / Measured software performance</p><h3 className="mt-4 font-display text-4xl font-black leading-[.98] md:text-5xl">Built to stay current.</h3></div>
              <p className="max-w-2xl text-lg leading-relaxed text-ink-600">Across a measured 1,000-endpoint fleet, QuantumCubeDB delivered faster state access, policy lookups and fleet visibility than SQLite.</p>
            </div>
          </div>

          <div className="mt-10 grid border border-ink-900 bg-white md:grid-cols-2 xl:grid-cols-5" role="list" aria-label="QuantumCubeDB operational-scale benchmark results">
            {scaleProof.map((item) => (
              <article key={item.label} role="listitem" className="border-b border-ink-100 p-5 last:border-b-0 md:border-r md:[&:nth-child(even)]:border-r-0 xl:border-b-0 xl:[&:nth-child(even)]:border-r xl:last:border-r-0">
                <h3 className="font-display text-base font-bold leading-tight">{item.label}</h3>
                <p className="mt-5 font-display text-3xl font-black text-electric">{item.quantum}</p>
                <p className="mt-1 text-xs text-ink-500">{item.unit}</p>
                <p className="mt-4 text-xs text-ink-500">SQLite: {item.sqlite}</p>
                <p className="mt-2 font-display text-sm font-bold text-ink-900">{item.outcome}</p>
              </article>
            ))}
          </div>

          <div className="mt-12"><ResponseChart /></div>
          <div className="mt-6 grid gap-6 lg:grid-cols-[1.1fr_.9fr]">
            <div className="border-l-2 border-electric bg-white px-5 py-5 text-sm leading-relaxed text-ink-700">These measurements focus on the compact operational-state workloads QuantumCubeDB is designed to serve inside LightningFlow.</div>
            <div className="border border-ink-100 bg-white p-5">
              <p className="font-mono text-[10px] uppercase tracking-[.16em] text-ink-500">Batch event capture</p>
              <p className="mt-2 font-display text-3xl font-black text-electric">48.3% faster</p>
              <p className="mt-2 text-sm text-ink-600">100 records per batch · 4 concurrent clients</p>
            </div>
          </div>
          <details id="technical-details" className="mt-10 scroll-mt-20 border border-ink-900 bg-white">
            <summary className="cursor-pointer list-none px-6 py-5 font-display text-2xl font-black marker:hidden">
              <span className="flex items-center justify-between gap-4">Benchmark methodology + results <span className="font-mono text-[10px] font-medium uppercase tracking-[.16em] text-electric">View details</span></span>
            </summary>
            <div className="border-t border-ink-900 px-6 py-7">
              <p className="max-w-4xl text-sm leading-relaxed text-ink-600">High-load response time is the response time within which 95% of measured operations completed. The response-time results below used 100,000 records, 2 KiB payloads and 16 concurrent clients. Batch capture used 100 records per batch and 4 concurrent clients.</p>
              <div className="mt-7 overflow-x-auto">
                <table className="min-w-[760px] w-full border-collapse text-left text-sm">
                  <caption className="sr-only">Exact raw response-time measurements against SQLite</caption>
                  <thead className="border-y border-ink-900 font-mono text-[10px] uppercase tracking-[.14em] text-ink-600">
                    <tr><th className="py-3 pr-5 font-medium">Workload</th><th className="py-3 pr-5 font-medium">QuantumCubeDB</th><th className="py-3 pr-5 font-medium">SQLite</th><th className="py-3 font-medium">Outcome</th></tr>
                  </thead>
                  <tbody>
                    {technicalRows.map((row) => (
                      <tr key={row[0]} className="border-b border-ink-100">
                        <td className="py-4 pr-5 font-semibold">{row[0]}</td>
                        <td className="py-4 pr-5 text-electric">{row[1]}</td>
                        <td className="py-4 pr-5">{row[2]}</td>
                        <td className="py-4 font-semibold">{row[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <h4 className="mt-10 font-display text-2xl font-black">1 MiB read characterisation</h4>
              <div className="mt-5 overflow-x-auto">
                <table className="min-w-[700px] w-full border-collapse text-left text-sm">
                  <caption className="sr-only">One mebibyte payload read-performance measurements against SQLite</caption>
                  <thead className="border-y border-ink-900 font-mono text-[10px] uppercase tracking-[.14em] text-ink-600"><tr><th className="py-3 pr-5 font-medium">Workload</th><th className="py-3 pr-5 font-medium">QuantumCubeDB</th><th className="py-3 pr-5 font-medium">SQLite</th><th className="py-3 font-medium">Outcome</th></tr></thead>
                  <tbody>{largePayloadReads.map((item) => <tr key={item.label} className="border-b border-ink-100"><td className="py-4 pr-5 font-semibold">{item.label}</td><td className="py-4 pr-5 text-electric">{item.quantum}</td><td className="py-4 pr-5">{item.sqlite}</td><td className="py-4 font-semibold">{item.outcome}</td></tr>)}</tbody>
                </table>
              </div>
              <p className="mt-4 text-sm text-ink-600">2,000 records · 1 MiB payloads · 50 concurrent clients · 3 independent trials · engine benchmark only</p>
              <p className="mt-7 text-sm leading-relaxed text-ink-600">Software-engine-only measurements against SQLite, independent of the FPGA batch tests described above. Results vary by workload, hardware, software version and deployment configuration. These results characterise the operational-state workloads QuantumCubeDB is designed for; they are not a universal database comparison.</p>
              <p className="mt-3 text-sm leading-relaxed text-ink-600">The benchmark does not measure HTTP, VPN tunnel, UAT or end-to-end customer traffic performance.</p>
            </div>
          </details>
        </div>
      </section>

      <section className="border-t border-ink-900 bg-ink-900 py-20 text-white md:py-28">
        <div className="lf-container">
          <Label dark>QuantumCubeDB™</Label>
          <div className="mt-6 grid gap-8 lg:grid-cols-[.9fr_1.1fr] lg:items-end">
            <h2 className="font-display text-4xl font-black leading-[.98] md:text-6xl">See clearly. Decide confidently.</h2>
            <div>
              <p className="max-w-2xl text-lg leading-relaxed text-ink-300">Fast operational state. Purpose-built telemetry processing. A foundation for informed decisions. Discover how QDB fits into LightningFlow.</p>
              <Link to="/platform" className="lf-btn mt-7 border border-white bg-white text-ink-900 hover:border-electric hover:bg-electric hover:text-white">Explore LightningFlow <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
